import React from 'react'
import styled from '@emotion/styled'
import { Button, Input, Modal, Popconfirm, Space } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  PortalArticleBlock,
  PortalSummaryTopic,
} from '../../../../models/PortalArtigoModel'
import { generateSummaryTopicsFromBlocks } from '../../helpers/anchor-utils'
import { PortalEditorField } from '../PortalEditorField'
import { portalEditorColors } from '../PortalEditorTheme'

interface SummaryTopicsEditorProps {
  blocks: PortalArticleBlock[]
  title?: string
  topics: PortalSummaryTopic[]
  onChange: (patch: { title?: string; topics?: PortalSummaryTopic[] }) => void
}

export function SummaryTopicsEditor({
  blocks,
  title,
  topics: value,
  onChange,
}: SummaryTopicsEditorProps) {
  const topics = value || []
  const updateTopic = (index: number, topic: PortalSummaryTopic) => {
    onChange(
      { topics: topics.map((currentTopic, currentIndex) => (currentIndex === index ? topic : currentTopic)) }
    )
  }
  const generateTopics = () => {
    const generated = generateSummaryTopicsFromBlocks(blocks)
    if (topics.length === 0) {
      onChange({ topics: generated })
      return
    }
    Modal.confirm({
      title: 'Substituir índice atual?',
      content: 'Os títulos e links editados manualmente serão substituídos pela estrutura atual dos tópicos.',
      okText: 'Gerar e substituir',
      cancelText: 'Cancelar',
      onOk: () => onChange({ topics: generated }),
    })
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <PortalEditorField
        label="Título do índice"
        helper="Se ficar vazio, o site exibirá “Conteúdo do artigo:”."
      >
        <Input
          value={title}
          placeholder="Conteúdo do artigo: (título padrão)"
          onChange={(event) => onChange({ title: event.target.value })}
        />
      </PortalEditorField>
      <Button
        icon={<PlusOutlined />}
        onClick={generateTopics}
      >
        Gerar índice pelos tópicos
      </Button>
      {topics.map((topic, index) => (
        <SummaryTopicCard key={index}>
          <SummaryTopicHeader>
            <SummaryTopicLabel>Tópico {index + 1}</SummaryTopicLabel>
            <Popconfirm
              title="Remover tópico"
              description="Tem certeza que deseja remover este tópico?"
              okText="Remover"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => onChange({ topics: topics.filter((_, i) => i !== index) })}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </SummaryTopicHeader>
          <TopicFieldsRow>
            <PortalEditorField label="Título do tópico">
              <Input
                value={topic.title}
                placeholder="Título do tópico"
                onChange={(event) => updateTopic(index, { ...topic, title: event.target.value })}
              />
            </PortalEditorField>
            <PortalEditorField label="Link do tópico">
              <Input
                value={topic.href}
                placeholder="#id-da-secao"
                onChange={(event) => updateTopic(index, { ...topic, href: event.target.value })}
              />
            </PortalEditorField>
          </TopicFieldsRow>
          <SubtopicsEditor
            value={topic.subtopics || []}
            onChange={(subtopics) => updateTopic(index, { ...topic, subtopics })}
          />
        </SummaryTopicCard>
      ))}
      <Button
        icon={<PlusOutlined />}
        onClick={() => onChange({ topics: [...topics, { title: '', href: '#' }] })}
      >
        Adicionar tópico
      </Button>
    </Space>
  )
}

function SubtopicsEditor({
  value,
  onChange,
}: {
  value: PortalSummaryTopic['subtopics']
  onChange: (subtopics: NonNullable<PortalSummaryTopic['subtopics']>) => void
}) {
  const subtopics = value || []
  const updateSubtopic = (index: number, subtopic: NonNullable<PortalSummaryTopic['subtopics']>[number]) => {
    onChange(
      subtopics.map((currentSubtopic, currentIndex) => (
        currentIndex === index ? subtopic : currentSubtopic
      ))
    )
  }

  return (
    <SubtopicsLayout>
      {subtopics.length > 0 ? (
        <SubtopicsHeader>
          <SubtopicsTitle>Subtópicos</SubtopicsTitle>
          <SubtopicsCount>{subtopics.length}</SubtopicsCount>
        </SubtopicsHeader>
      ) : null}
      {subtopics.map((subtopic, index) => (
        <SubtopicCard key={index}>
          <SubtopicHeader>
            <SubtopicIdentity>
              <SubtopicNumber>{index + 1}</SubtopicNumber>
              <span>Subtópico {index + 1}</span>
            </SubtopicIdentity>
            <Popconfirm
              title="Remover subtópico"
              description="Tem certeza que deseja remover este subtópico?"
              okText="Remover"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => onChange(subtopics.filter((_, i) => i !== index))}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </SubtopicHeader>
          <SubtopicFieldsRow>
            <PortalEditorField label="Título do subtópico">
              <Input
                value={subtopic.title}
                placeholder={`Título do subtópico ${index + 1}`}
                onChange={(event) => updateSubtopic(index, { ...subtopic, title: event.target.value })}
              />
            </PortalEditorField>
            <PortalEditorField label="Link do subtópico">
              <Input
                value={subtopic.href}
                placeholder="#id-da-secao"
                onChange={(event) => updateSubtopic(index, { ...subtopic, href: event.target.value })}
              />
            </PortalEditorField>
          </SubtopicFieldsRow>
        </SubtopicCard>
      ))}
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() => onChange([...subtopics, { title: '', href: '#' }])}
      >
        Adicionar subtópico
      </Button>
    </SubtopicsLayout>
  )
}

const SummaryTopicCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 12px;
  border: 1px solid ${portalEditorColors.border};
  border-radius: 8px;
  background: ${portalEditorColors.hover};
`

const SummaryTopicHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const SummaryTopicLabel = styled.span`
  color: #595959;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`

const TopicFieldsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 0.7fr);
  gap: 12px;
  width: 100%;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const SubtopicsLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
  padding: 12px;
  border: 1px solid ${portalEditorColors.border};
  border-radius: 8px;
  background: ${portalEditorColors.surface};
`

const SubtopicsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 2px;
`

const SubtopicsTitle = styled.span`
  color: ${portalEditorColors.navy};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
`

const SubtopicsCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #fff0e9;
  color: ${portalEditorColors.orange};
  font-size: 12px;
  font-weight: 700;
`

const SubtopicCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid ${portalEditorColors.border};
  border-left: 3px solid ${portalEditorColors.orange};
  border-radius: 6px;
  background: ${portalEditorColors.surface};
`

const SubtopicHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const SubtopicIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${portalEditorColors.navy};
  font-size: 12px;
  font-weight: 700;
`

const SubtopicNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${portalEditorColors.navy};
  color: ${portalEditorColors.surface};
  font-size: 12px;
`

const SubtopicFieldsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 0.7fr);
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`
